import React, { useState } from 'react';
import { MessageSquare, X, Send, Bot, Sparkles, ArrowUp } from 'lucide-react';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { ChatMessage } from '../../types/chat';
import { initialChatChips, chatPresetResponses } from '../../data/chatPresets';
import { cn } from '../../utils/cn';

export const FloatingActionHub: React.FC<{ onOpenConsultation?: () => void }> = ({ onOpenConsultation }) => {
  const { isScrolled } = useScrollPosition();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'assistant',
      text: 'Hello! I am Technook’s Digital Assistant. How can we help accelerate your technology or business goals today?',
      timestamp: 'Just now',
      actionChips: initialChatChips,
    },
  ]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChipClick = (actionKey: string, label: string) => {
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: label,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const preset = chatPresetResponses[actionKey];
    const assistantMsg: ChatMessage = {
      id: (Date.now() + 1).toString(),
      sender: 'assistant',
      text: preset ? preset.response : 'Thank you for contacting Technook Global. Our lead team will respond promptly!',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      actionChips: preset?.followUpChips,
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);

    if (actionKey === 'book' && onOpenConsultation) {
      onOpenConsultation();
    }
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: input.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const assistantMsg: ChatMessage = {
      id: (Date.now() + 1).toString(),
      sender: 'assistant',
      text: `Thanks for asking! Technook Global provides end-to-end software development, marketing, lead gen, and admin support. You can reach our Parañaque team directly at info@technookglobal.com or call +639 56 928 4326.`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      actionChips: [
        { label: 'Book Consultation', actionKey: 'book' },
        { label: 'Request a Quote', actionKey: 'quote' },
      ],
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
  };

  return (
    <>
      {/* Paired Floating Action Hub (Scroll To Top + AI Chat Trigger) */}
      <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex items-center gap-2 sm:gap-2.5">
        {/* Floating Scroll To Top Button */}
        {isScrolled && (
          <button
            onClick={scrollToTop}
            aria-label="Scroll to top"
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#184166] dark:bg-slate-900/90 border border-white/20 dark:border-slate-700/80 text-white hover:text-sky-300 shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center animate-in fade-in zoom-in duration-200"
          >
            <ArrowUp size={18} />
          </button>
        )}

        {/* Floating AI Chat Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Open Technook AI Assistant Chat"
          className="px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-full bg-gradient-to-r from-[#4a82c2] via-[#356aa5] to-[#184166] text-white shadow-2xl shadow-[#4a82c2]/40 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-2.5 group border border-white/20"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#08adae] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#08adae]"></span>
          </span>
          {isOpen ? <X size={18} /> : <MessageSquare size={18} />}
          <span className="hidden md:inline text-xs font-bold tracking-wide pl-0.5 pr-1">Assistant</span>
        </button>
      </div>

      {/* Floating Chat Assistant Modal */}
      {isOpen && (
        <div className="fixed bottom-20 right-5 sm:bottom-24 sm:right-6 z-50 w-[calc(100vw-2.5rem)] max-w-sm sm:max-w-md bg-white/95 dark:bg-slate-950/95 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl shadow-blue-950/10 dark:shadow-sky-500/20 backdrop-blur-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col h-[480px] sm:h-[520px]">
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-[#184166] to-[#24527f] border-b border-slate-700 flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/10 border border-white/20 text-white">
                <Bot size={20} />
              </div>
              <div>
                <h4 className="text-sm font-bold flex items-center gap-1.5">
                  Technook Assistant <Sparkles size={14} className="text-[#08adae] animate-pulse" />
                </h4>
                <p className="text-[11px] text-[#08adae] font-medium flex items-center gap-1.5 mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-[#08adae] inline-block shrink-0"></span>
                  <span>Online • Parañaque HQ</span>
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1 text-slate-300 hover:text-white rounded-lg">
              <X size={18} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-slate-50/50 dark:bg-slate-950/50 scrollbar-thin">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn('flex flex-col max-w-[85%]', msg.sender === 'user' ? 'ml-auto items-end' : 'mr-auto items-start')}
              >
                <div
                  className={cn(
                    'p-3.5 rounded-2xl text-xs leading-relaxed shadow-sm',
                    msg.sender === 'user'
                      ? 'bg-[#4a82c2] text-white rounded-br-none font-medium'
                      : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-none shadow-md'
                  )}
                >
                  <p>{msg.text}</p>
                </div>
                <span className="text-[10px] text-slate-400 mt-1 px-1">{msg.timestamp}</span>

                {/* Quick Action Chips */}
                {msg.actionChips && msg.actionChips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {msg.actionChips.map((chip) => (
                      <button
                        key={chip.actionKey}
                        onClick={() => handleChipClick(chip.actionKey, chip.label)}
                        className="px-2.5 py-1 bg-[#4a82c2]/10 hover:bg-[#4a82c2]/20 border border-[#4a82c2]/30 text-[#356aa5] dark:text-sky-400 text-[11px] font-semibold rounded-full transition-colors"
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Input Form */}
          <form onSubmit={handleSend} className="p-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question or type details..."
              className="flex-1 px-3.5 py-2.5 bg-slate-100 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-[#4a82c2]"
            />
            <button
              type="submit"
              className="p-2.5 rounded-xl bg-gradient-to-r from-[#4a82c2] to-[#184166] text-white hover:opacity-90 transition-opacity"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
